import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import ProfileTabs from '../components/ProfileTabs';
import { InstallPrompt } from '../components/InstallPrompt';
import { TimeSavedCard } from '../components/TimeSavedCard';
import { 
  User, 
  Briefcase, 
  CreditCard, 
  FileText, 
  ChevronRight,
  Plus,
  Upload,
  AlertTriangle,
  Zap,
  Sparkles,
  Crown,
  Chrome,
  Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [missing, setMissing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, missingRes] = await Promise.all([
        axios.get(`${API}/profile`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/profile/missing`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setProfile(profileRes.data);
      setStats(statsRes.data);
      setMissing(missingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="font-mono text-lg ascii-loader mb-4"></div>
          <p className="text-muted-foreground">LOADING PROFILE...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-8 py-8" data-testid="dashboard">
      <InstallPrompt />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-1">WELCOME, {user?.name?.split(' ')[0]?.toUpperCase()}</h1>
          <p className="text-muted-foreground">Build your profile. Autofill everything.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/documents">
            <Button variant="outline" className="uppercase text-xs tracking-wider" data-testid="upload-docs-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Docs
            </Button>
          </Link>
          <Link to="/fill" className="hidden md:inline-block">
            <Button variant="ghost" size="sm" className="uppercase text-[10px] tracking-widest text-muted-foreground" data-testid="fill-helper-btn">
              Copy Helper
            </Button>
          </Link>
          <Link to="/extension">
            <Button variant="outline" className="uppercase text-xs tracking-wider relative" data-testid="install-extension-btn">
              <Chrome className="w-4 h-4 mr-2" />
              Install Extension
              <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[8px] px-1 py-0.5 font-bold tracking-wider">NEW</span>
            </Button>
          </Link>
          <Link to="/applications">
            <Button className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="new-app-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </Link>
        </div>
      </div>

      {/* Extension promo strip */}
      <ExtensionPromo />

      {/* Welcome banner for free users */}
      {stats?.subscription_tier !== 'pro' && (
        <WelcomeBanner
          documentsCount={stats?.documents_count || 0}
          autofillsUsed={stats?.autofills_used || 0}
          autofillsLimit={stats?.autofills_limit || 5}
          profileCompletion={stats?.profile_completion || 0}
        />
      )}

      <TimeSavedCard timeSaved={stats?.time_saved} tier={stats?.subscription_tier} />

      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-px bg-border mb-8 border border-border">
        <StatCard
          label="PROFILE STRENGTH"
          value={`${stats?.profile_completion || 0}%`}
          icon={<User className="w-4 h-4" />}
          progress={stats?.profile_completion || 0}
        />
        <StatCard
          label="DOCUMENTS"
          value={stats?.documents_count || 0}
          icon={<FileText className="w-4 h-4" />}
        />
        <StatCard
          label="APPLICATIONS"
          value={stats?.applications_count || 0}
          icon={<Briefcase className="w-4 h-4" />}
        />
        <StatCard
          label="AUTOFILLS"
          value={`${stats?.autofills_used || 0}/${stats?.autofills_limit === 999999 ? '∞' : stats?.autofills_limit || 2}`}
          icon={<CreditCard className="w-4 h-4" />}
          tier={stats?.subscription_tier}
        />
      </div>

      {/* Missing Data Engine + Template Readiness */}
      {missing && (
        <div className="grid md:grid-cols-2 gap-px bg-border mb-8 border border-border">
          <MissingDataPanel missing={missing} />
          <TemplateReadinessPanel missing={missing} />
        </div>
      )}

      {/* Profile Tabs */}
      <ProfileTabs profile={profile} onSaved={fetchData} />

      {/* Quick Actions */}
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <QuickAction
          title="UPLOAD DOCUMENTS"
          description="Let AI extract your data automatically"
          icon={<Upload className="w-5 h-5" />}
          href="/documents"
        />
        <QuickAction
          title="START APPLICATION"
          description="Auto-fill a new form in seconds"
          icon={<FileText className="w-5 h-5" />}
          href="/applications"
        />
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, progress, tier }) {
  return (
    <div className="bg-background p-6" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">{icon}</span>
      </div>
      <div className="font-heading text-2xl mb-2">{value}</div>
      {progress !== undefined && (
        <Progress value={progress} className="h-1" />
      )}
      {tier && (
        <span className={`text-xs uppercase ${tier === 'pro' ? 'text-accent' : 'text-muted-foreground'}`}>
          {tier} PLAN
        </span>
      )}
    </div>
  );
}

function QuickAction({ title, description, icon, href }) {
  return (
    <Link to={href} className="block" data-testid={`quick-action-${title.toLowerCase().replace(' ', '-')}`}>
      <div className="border border-border p-6 hover:bg-secondary/50 transition-colors group flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 border border-border flex items-center justify-center text-primary">
            {icon}
          </div>
          <div>
            <h3 className="font-heading text-sm">{title}</h3>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

function MissingDataPanel({ missing }) {
  const top = (missing?.top_suggestions || []).slice(0, 5);
  return (
    <div className="bg-background p-6" data-testid="missing-data-panel">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">MISSING DATA ENGINE</span>
        <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
      </div>
      {top.length === 0 ? (
        <div>
          <div className="font-heading text-lg mb-1">PROFILE LOOKS COMPLETE</div>
          <p className="text-xs text-muted-foreground">You have all the fields needed for every template.</p>
        </div>
      ) : (
        <>
          <div className="font-heading text-lg mb-1">FILL THESE NEXT</div>
          <p className="text-xs text-muted-foreground mb-4">
            These fields unlock the most templates.
          </p>
          <div className="space-y-1.5">
            {top.map((s) => (
              <div key={s.field} className="flex items-center justify-between text-xs py-1 border-b border-border/60" data-testid={`missing-${s.field}`}>
                <span className="font-mono uppercase tracking-wider truncate">{s.field.replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground text-[10px] uppercase tracking-widest flex-shrink-0 ml-3">
                  Unlocks {s.blocks_templates} template{s.blocks_templates > 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TemplateReadinessPanel({ missing }) {
  const templates = Object.entries(missing?.template_readiness || {});
  return (
    <div className="bg-background p-6" data-testid="template-readiness-panel">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">TEMPLATE READINESS</span>
        <Zap className="w-4 h-4 text-primary" />
      </div>
      <div className="font-heading text-lg mb-4">AUTOFILL CAPACITY</div>
      <div className="space-y-3">
        {templates.map(([key, t]) => (
          <Link to="/applications" key={key} className="block group" data-testid={`readiness-${key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors truncate">{t.title}</span>
              <span className="font-mono">{t.ready_pct}%</span>
            </div>
            <div className="h-1 bg-secondary">
              <div className={`h-full transition-all ${t.ready_pct === 100 ? 'bg-accent' : 'bg-primary'}`} style={{ width: `${t.ready_pct}%` }} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function WelcomeBanner({ documentsCount, autofillsUsed, autofillsLimit, profileCompletion }) {
  const remaining = Math.max(0, autofillsLimit - autofillsUsed);
  const isNewUser = documentsCount === 0 && profileCompletion < 20;

  if (isNewUser) {
    return (
      <div className="mb-8 border border-border bg-secondary/30 p-6" data-testid="welcome-banner">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 border border-border bg-background flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl mb-2">WELCOME — HERE'S HOW TO GET STARTED</h2>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <StepCard num="1" title="UPLOAD A DOC" desc="Upload your resume, ID, or payslip. AI parses it in seconds." link="/documents" cta="UPLOAD NOW" />
              <StepCard num="2" title="AUTOFILL A FORM" desc="Create an application → click Autofill → all fields pre-filled." link="/applications" cta="CREATE ONE" />
              <StepCard num="3" title="INSTALL EXTENSION" desc="One-click autofill on any form on the web. Chrome/Edge/Brave." link="/extension" cta="INSTALL NOW" />
            </div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-5">
              FREE PLAN: {remaining} of {autofillsLimit} autofills remaining this month
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Returning free user — subtle status/upgrade nudge
  return (
    <div className="mb-8 border border-border bg-background p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="returning-free-banner">
      <div className="flex items-center gap-3 text-xs">
        <Crown className="w-4 h-4 text-muted-foreground" />
        <span className="uppercase tracking-widest text-muted-foreground">
          FREE PLAN · {remaining} of {autofillsLimit} autofills left this month
        </span>
      </div>
      <Link to="/upgrade">
        <Button size="sm" className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="get-more-autofills-btn">
          <Zap className="w-3.5 h-3.5 mr-2" />
          Get More Autofills · $9.99/mo
        </Button>
      </Link>
    </div>
  );
}

function StepCard({ num, title, desc, link, cta }) {
  return (
    <Link to={link} className="border border-border bg-background p-4 hover:border-primary transition-colors block group" data-testid={`step-${num}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">STEP {num}</div>
      <div className="font-heading text-sm mb-2">{title}</div>
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{desc}</p>
      <div className="text-[10px] uppercase tracking-widest text-primary group-hover:underline flex items-center gap-1">
        {cta} <ChevronRight className="w-3 h-3" />
      </div>
    </Link>
  );
}

function ExtensionPromo() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('ext_promo_dismissed') === '1');
  if (dismissed) return null;
  return (
    <div
      className="mb-6 border-2 border-border p-5 flex flex-col md:flex-row md:items-center gap-4"
      style={{ boxShadow: '4px 4px 0 0 hsl(var(--primary))' }}
      data-testid="extension-promo"
    >
      <div className="w-11 h-11 bg-primary text-primary-foreground flex items-center justify-center shrink-0">
        <Chrome className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">NEW · CHROME EXTENSION</div>
        <div className="font-heading text-lg leading-tight mb-1">One-click autofill on any form. Anywhere.</div>
        <p className="text-xs text-muted-foreground">
          Install our extension. It reads your Life Profile and fills forms with one click — no copy-paste.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Link to="/extension">
          <Button size="sm" className="brutalist-shadow-sm uppercase text-xs tracking-widest" data-testid="promo-install-extension-btn">
            <Download className="w-3.5 h-3.5 mr-2" /> INSTALL
          </Button>
        </Link>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { localStorage.setItem('ext_promo_dismissed', '1'); setDismissed(true); }}
          className="uppercase text-[10px] tracking-widest text-muted-foreground"
          data-testid="promo-dismiss-btn"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
